import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { AXES } from './axisGrid';
import { useEditableTriple } from './editableTriple';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStableText } from '../../components/StableText';

// WHERE THE PART STARTS, WHAT HAPPENS, WHERE IT ENDS — read left to right.
//
// The box that defines a movement used to hold two controls and nothing else: which way the
// part comes in, and how far. Both are true and neither is legible — "40 mm along +Z" is a
// number you can only check against a picture, and the picture is 460 pixels of chair. The
// two ends of the move were derivable from them, which is exactly why nobody derived them.
//
// So the movement is put between its ends, in that order, with a rule down each side. The
// order is the argument: the middle column is the only one you can edit, and the two beside it
// are what editing it does. Everything is read-only on the outside — a final position is
// answered on the part itself, by dragging it or typing into its card, and a starting position
// is not a field anywhere because it IS the axis and the travel. Offering it as a fourth way
// to say the same thing would be three fields that can disagree.
//
// AND THE MIDDLE COLUMN NAMES WHAT ACTUALLY CHANGED. Six numbers a side, and on a typical
// fitting one of them moves; a reader comparing two columns of six is doing the diff by eye,
// every time, to find the single line that matters. It is spelled out instead — and a gesture
// that turns a part without moving it, which is most placings, stops being invisible.

// ONE ROW OF THREE FIELDS, and it is a component rather than a builder because it holds the
// half-typed state (see useEditableTriple) and a hook cannot be called from a loop inside
// another render. Four of these stand on the page — two ends, position and orientation — and
// each keeps its own typing independently, which is right: they are four separate answers.
function EditableRow({ head, cell, values, about, onCommit }: {
  head: ReactNode;
  cell: string;
  values: number[];
  about: string;
  onCommit: (next: [number, number, number]) => void;
}) {
  const { text, at, commit, revert } = useEditableTriple(values, onCommit, about);
  return (
    <tr>
      {head}
      {AXES.map(({ axis, colour }, i) => (
        <td key={axis} className={`${cell} p-0`}>
          <input
            type="text"
            inputMode="decimal"
            value={text[i] ?? ''}
            style={{ color: colour }}
            onChange={(e) => at(i, e.target.value)}
            // Leaving the field is the commit and Enter is leaving it early; Escape puts back
            // what the part actually says. The same three keys the card's panel answers to,
            // because it is the same question asked of the same numbers.
            onBlur={() => commit(text)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit(text);
              if (e.key === 'Escape') revert();
            }}
            className="h-full w-full bg-transparent px-1 py-1 text-center text-[11px] font-bold leading-none tabular-nums outline-none focus:bg-slate-100"
          />
        </td>
      ))}
    </tr>
  );
}

export type PoseRead = {
  // Already in the units they are shown in: centimetres and degrees. The conversion belongs
  // where the pose is computed, not here — this draws numbers.
  pos: number[];
  rot: number[];
};

export function MovementSummary({
  start, end, now, moment, part, busy, still, onStart, onEnd,
}: {
  // Null on a still row, which has no start — see `still`.
  start?: PoseRead | null;
  end: PoseRead | null;
  // WHICH PART ALL OF THIS IS ABOUT, name and letter, over the whole bar.
  //
  // Six numbers a side, a direction, a distance — and nothing said whose. A fitting draws two
  // parts and only one of them travels, so "position" and "orientation" were readings of an
  // unnamed thing, on a page where the part that does NOT move has coordinates of its own in
  // the card panel. Named once at the top, because it is one answer about one part and three
  // copies of the name would be the same word three times.
  part?: string;
  // WHAT A TYPED END MEANS, and the two are not the same question.
  //
  // FINAL is where the part comes to rest: typing it moves the part, and the movement — which
  // way in, how far — is untouched, so the starting position follows it across.
  //
  // STARTING is where it comes FROM, and the part's rest is untouched: what changes is the
  // movement between them, because that is the only thing left that can. The direction stops
  // being one of six words the moment a typed start is off-axis, and the toggle says so by
  // lighting none of them rather than by rounding to the nearest.
  //
  // Both in the units shown — centimetres and degrees. Absent, the tables are readings.
  onStart?: (pos: [number, number, number], rot: [number, number, number]) => void;
  onEnd?: (pos: [number, number, number], rot: [number, number, number]) => void;
  // THE CALL THAT ANSWERS THIS IS OUT. Every column goes to a spinner, controls included.
  //
  // All six of these numbers are about to change: Phase 5 now answers which way the part
  // comes in and how far, and those two are what the two ends are computed FROM. Left
  // standing they would be the previous answer, drawn in the same weight as a settled one —
  // and a table of numbers is the most confident thing on the page, which makes it the worst
  // thing to leave asserting through a wait.
  //
  // The controls go too, and that is deliberate rather than tidiness: "+Z, 50 mm" is exactly
  // what the call is about to overwrite, so showing it while asking is the phase stating as
  // settled the thing it has gone to ask about. Same argument as the kind chip's.
  busy?: boolean;
  // WHERE THE PART IS AT THIS INSTANT, for the middle column — and `t`, which is the column's
  // own heading: "(t=0.42)", moving with it.
  //
  // The middle used to hold the two controls that DEFINED the movement — which way in, how
  // far — and then the movement stopped being defined that way: both ends are typed now, and
  // both are answered, so the direction and the distance became readings of the tables either
  // side. What belongs between two ends is not a third statement of them but the thing
  // neither can say — where the part has got to — and it is the only reading here that moves
  // while the scrubber does.
  //
  // Absent, the column is not drawn and the bar is two halves with one rule.
  now?: PoseRead | null;
  moment?: number;
  // A PART THAT DOES NOT MOVE, drawn as ONE column headed "position" instead of two ends and
  // a middle. It is the same table with the same units — that is the point of drawing it here
  // rather than somewhere else — and a start and a final for a part that stays put would be
  // the same six numbers twice, which reads as a movement of zero rather than as no movement.
  still?: boolean;
}) {
  const { t } = useLanguage();
  const st = useStableText();

  // A REAL TABLE, WITH ITS LINES DRAWN. Six numbers in a three-by-two grid, and until the
  // rules were there the only thing saying which reading a number belonged to was how far
  // down the column it sat — fine while both rows are full, and immediately ambiguous beside a
  // second table whose rows do not line up with the first's.
  //
  // `border-collapse` is what makes inner and outer one weight instead of two: adjacent cells
  // share an edge rather than drawing one each, so nothing doubles up at the joins.
  const box = 'border border-slate-200';
  // Wide enough for a signed two-decimal centimetre, so the columns hold still as the numbers
  // in them change.
  //
  // A HEIGHT, so a cell somebody can type into and a cell they cannot are the same size. The
  // outer two columns hold inputs and the middle one holds text, and an input carries its own
  // metrics — the middle table came out a couple of pixels shorter per row, which over two
  // rows and a header is a column visibly not lining up with its neighbours.
  const num = `${box} h-7 w-[4.5rem] px-1 py-1 text-center text-[11px] font-bold leading-none tabular-nums`;

  const colTitle = (text: string, at: string) => (
    <p className="mb-1 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {text}
      {/* NOT UPPERCASED, because it is not a word: t is the scrubber's own name for where you
          are in the movement, and "T=0" reads as a constant rather than as the left-hand end
          of the slider under the viewer. */}
      <span className="ml-1 font-normal normal-case text-slate-400">
        ({at})
      </span>
    </p>
  );

  // THE READING NAMES ITS OWN ROW, on the left, where the two rows can be told apart without
  // counting downwards. Stacked captions put the name of a row ABOVE it, which works for one
  // table and stops working the moment there are two side by side: the eye pairs things that
  // are level with each other, and a caption is level with the row above it.
  const rowHead = (label: ReactNode) => (
    <th
      scope="row"
      className={`${box} px-2 py-1 text-right text-[9px] font-semibold uppercase leading-none tracking-wide text-slate-400`}
    >
      {label}
    </th>
  );

  const row = (label: ReactNode, values: number[]) => (
    <tr>
      {rowHead(label)}
      {AXES.map(({ axis, colour }, i) => (
        <td key={axis} style={{ color: colour }} className={num}>
          {values[i]}
        </td>
      ))}
    </tr>
  );

  const side = (pose: PoseRead, write?: (
    pos: [number, number, number], rot: [number, number, number],
  ) => void, about?: string) => (
    <div className="flex flex-col items-center">
      <table className="border-collapse">
        <thead>
          <tr>
            {/* The corner cell, which names nothing: the column headers are axes and the row
                headers are readings, and there is no word that is both. Drawn all the same,
                so the grid has no notch out of it. */}
            <th className={box} />
            {AXES.map(({ axis, colour }) => (
              <th
                key={axis}
                scope="col"
                style={{ color: colour }}
                className={`${box} px-1 py-1 text-center text-[11px] font-bold leading-none`}
              >
                {axis.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {write ? (
            <>
              <EditableRow
                head={rowHead(st('notice.assembly.posRow'))}
                cell={num}
                values={pose.pos}
                about={`${about}-pos`}
                onCommit={(next) => write(next, pose.rot as [number, number, number])}
              />
              <EditableRow
                head={rowHead(st('notice.assembly.rotRow'))}
                cell={num}
                values={pose.rot}
                about={`${about}-rot`}
                onCommit={(next) => write(pose.pos as [number, number, number], next)}
              />
            </>
          ) : (
            <>
              {row(st('notice.assembly.posRow'), pose.pos)}
              {row(st('notice.assembly.rotRow'), pose.rot)}
            </>
          )}
        </tbody>
      </table>
    </div>
  );

  // EQUAL THIRDS, EACH WITH ITS CONTENTS CENTRED IN IT. Laid out by their own widths the
  // three columns sat wherever the middle one's controls left them — a segmented toggle of six
  // buttons is far wider than a table of three numbers, so the two ends were pushed out to the
  // margins and the whole row read as a wide thing with two labels stuck on it. Thirds make
  // the sides symmetrical whatever the middle grows into.
  //
  // THE RULES ARE DRAWN, not borrowed from the middle column's edges. As borders they were
  // exactly as tall as that column's contents — which is the grid row and nothing else, so
  // they began under the bar's own title and stopped above its last table, floating in the
  // middle of a box they are supposed to divide. Absolutely placed on the thirds instead,
  // reaching past the container's padding at both ends, they cut the whole bar the way the
  // rule between the two bars cuts the whole container.
  const third = 'flex flex-col items-center px-4';
  const rule = 'pointer-events-none absolute -top-4 -bottom-4 w-px bg-slate-200';

  // Tall enough to stand in for a three-row table, so the bar does not collapse and spring
  // back around a wait it is already announcing.
  const waiting = (
    <span className="flex min-h-[4.5rem] items-center justify-center text-slate-400">
      <Loader2 className="h-4 w-4 animate-spin" />
    </span>
  );

  if (still) {
    return (
      <div className="flex flex-col">
        {part && (
          <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
            {part}
          </p>
        )}
        <div className="flex justify-center">
          <div className={third}>
            {colTitle(t('notice.assembly.stillPos'), '')}
            {busy ? waiting : end && side(end)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col">
      {/* On the column edges exactly, wherever those are: a bar with no middle is two halves
          with one rule between them, not three columns one of which is blank. */}
      {now ? (
        <>
          <span className={`${rule} left-1/3`} />
          <span className={`${rule} left-2/3`} />
        </>
      ) : (
        <span className={`${rule} left-1/2`} />
      )}
      {part && (
        <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
          {part}
        </p>
      )}
      <div className={`grid items-stretch ${now ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <div className={third}>
          {colTitle(t('notice.assembly.startPos'), 't=0')}
          {busy ? waiting : start && side(start, onStart, 'start')}
        </div>

        {/* WHERE IT HAS GOT TO, headed by the t it is a reading of — which is how it says it
            is a reading and not a third setting: "(t=0.42)" moves with the scrubber, and its
            two neighbours are headed by the two ends that do not.

            Read-only, and it is the one column that could not be anything else: what would
            typing a position at t=0.42 mean? Either the seat or the start, depending on a
            number nobody is looking at. */}
        {now && (
          <div className={third}>
            {colTitle(t('notice.assembly.nowPos'), `t=${(moment ?? 0).toFixed(2)}`)}
            {busy ? waiting : side(now)}
          </div>
        )}

        <div className={third}>
          {colTitle(t('notice.assembly.finalPos'), 't=1')}
          {busy ? waiting : end && side(end, onEnd, 'end')}
        </div>
      </div>
    </div>
  );
}
